import type { IDBPDatabase, IDBPTransaction } from 'idb';
import { startPlannedSession, completeExecution, cancelExecution, validExecutedSession, validPlannedSession,
  validWorkingSet, validExerciseRevision, validId, validRevision, trainingFailure as failure } from '../domain/training.ts';
import type { ExecutedSession, WorkingSet, TrainingResult } from '../domain/training.ts';
import type { TrainingRepository, TrainingContext, StartSessionCommand, AddWorkingSetCommand, CompleteSessionCommand,
  CancelSessionCommand, CancelSessionEffect, TrainingCommitResult, CancelSessionCommitResult, ExecutedSessionView, SessionHistoryView } from '../ports/training-repository.ts';
import { openFenixDatabase, storageFailure, validDatasetControl } from './fenix-db.ts';
import type { FenixSchema } from './fenix-db.ts';
import { canonical } from './canonical.ts';
import { validMutation, validTrainingReceipt } from './training-receipts.ts';
import type { StoredTrainingReceipt } from './training-receipts.ts';

export type SessionFault = 'after-claim' | 'after-execution' | 'after-set' | 'after-receipt' | 'after-control' | 'lost-ack';
export class SessionLabFaults { next: SessionFault | null = null; take() { const f = this.next; this.next = null; return f; } }
const stores = ['control', 'training_executions', 'training_working_sets', 'training_receipts',
  'training_planned_sessions', 'training_planned_session_history', 'training_exercise_revisions'] as const;
type Tx = IDBPTransaction<FenixSchema, typeof stores, 'readonly' | 'readwrite'>;
type Port = Pick<TrainingRepository, 'readActiveExecution' | 'readExecution' | 'readExecutionForPlannedSession' |
  'listExecutionHistory' | 'listSessionHistory' | 'startSession' | 'addWorkingSet' | 'completeSession' | 'cancelSession'>;
type Command = StartSessionCommand | AddWorkingSetCommand | CompleteSessionCommand | CancelSessionCommand;
type Commit = TrainingCommitResult & { effect?: CancelSessionEffect };
const scope = (datasetId: string) => IDBKeyRange.bound([datasetId], [datasetId, []]);
function inject(fault: SessionFault | null, point: SessionFault) { if (fault === point) throw new DOMException('Synthetic session abort', 'AbortError'); }

export class IndexedTrainingSessions implements Port {
  private available = true;
  constructor(private readonly db: IDBPDatabase<FenixSchema>, private readonly faults = new SessionLabFaults()) {
    db.addEventListener('versionchange', () => this.close());
    db.addEventListener('close', () => { this.available = false; });
  }
  close() { this.available = false; this.db.close(); }
  private async transaction<M extends 'readonly' | 'readwrite', T>(mode: M, run: (tx: IDBPTransaction<FenixSchema, typeof stores, M>) => Promise<TrainingResult<T>>): Promise<TrainingResult<T>> {
    if (!this.available) return failure('STORAGE_UNAVAILABLE');
    let tx: IDBPTransaction<FenixSchema, typeof stores, M> | undefined;
    try {
      tx = this.db.transaction(stores, mode, { durability: 'strict' }); void tx.done.catch(() => {});
      const result = await run(tx);
      if (!result.ok) { tx.abort(); await tx.done.catch(() => {}); return result; }
      await tx.done; return result;
    } catch (error) {
      if (tx) { try { tx.abort(); } catch { /* settled */ } await tx.done.catch(() => {}); }
      return storageFailure(error);
    }
  }
  private async control(tx: Tx, context?: TrainingContext) {
    const c = await tx.objectStore('control').get('control');
    if (!validDatasetControl(c)) return failure('CORRUPTION_DETECTED');
    if (c.restoreFence !== null) return failure('RESTORE_IN_PROGRESS');
    if (c.recoveryDatasetId !== null) return failure('CORRUPTION_DETECTED');
    if (context && (context.datasetId !== c.activeDatasetId || context.controlEpoch !== c.controlEpoch)) return failure('DATASET_STALE');
    return { ok: true as const, value: c };
  }
  // An open execution IS the active claim. Reading and writing this store in one
  // overlapping readwrite transaction serializes every lifecycle/set operation.
  private async snapshot(tx: Tx, datasetId: string) {
    const executions: ExecutedSession[] = [], sets: { record: WorkingSet; position: number }[] = [];
    const plans = new Set<string>(); let active = 0;
    let cursor = await tx.objectStore('training_executions').openCursor(scope(datasetId));
    while (cursor) {
      const row = cursor.value;
      if (!row || row.datasetId !== datasetId || !validExecutedSession(row.record) || cursor.key[1] !== row.record.executionId
        || plans.has(row.record.plannedSessionId)) return failure('CORRUPTION_DETECTED');
      const e = row.record;
      const p = await tx.objectStore('training_planned_sessions').get([datasetId, e.plannedSessionId]);
      const h = await tx.objectStore('training_planned_session_history').get([datasetId, e.plannedSessionId, e.planRevisionAtStart]);
      if (!p || !h || p.datasetId !== datasetId || h.datasetId !== datasetId || !validPlannedSession(p.record)
        || !validPlannedSession(h.record) || p.record.sessionId !== e.plannedSessionId || p.record.explicitOmission
        || h.record.sessionId !== e.plannedSessionId || h.record.revision !== e.planRevisionAtStart
        || canonical(p.record) !== canonical(h.record)) return failure('CORRUPTION_DETECTED');
      if (e.endedInstant === undefined) active += 1;
      if (active > 1) return failure('CORRUPTION_DETECTED');
      plans.add(e.plannedSessionId); executions.push(e); cursor = await cursor.continue();
    }
    let setCursor = await tx.objectStore('training_working_sets').openCursor(scope(datasetId));
    while (setCursor) {
      const row = setCursor.value;
      if (!row || row.datasetId !== datasetId || !validWorkingSet(row.record) || setCursor.key[1] !== row.record.workingSetId
        || !validRevision(row.position) || !executions.some(e => e.executionId === row.record.executedSessionId)
        || sets.some(s => s.record.executedSessionId === row.record.executedSessionId && s.position === row.position)) return failure('CORRUPTION_DETECTED');
      sets.push(row); setCursor = await setCursor.continue();
    }
    sets.sort((a, b) => a.position - b.position);
    return { ok: true as const, value: { executions, sets } };
  }
  private views() {
    return this.transaction('readonly', async tx => {
      const c = await this.control(tx); if (!c.ok) return c;
      const s = await this.snapshot(tx, c.value.activeDatasetId); if (!s.ok) return s;
      const context = { datasetId: c.value.activeDatasetId, controlEpoch: c.value.controlEpoch };
      return { ok: true as const, value: s.value.executions.map(execution => ({ execution, context,
        workingSets: s.value.sets.filter(s => s.record.executedSessionId === execution.executionId).map(s => s.record) })) };
    });
  }
  async readActiveExecution(): Promise<TrainingResult<ExecutedSessionView | null>> {
    const r = await this.views(); return r.ok ? { ok: true, value: r.value.find(v => v.execution.endedInstant === undefined) ?? null } : r;
  }
  async readExecution(id: string): Promise<TrainingResult<ExecutedSessionView>> {
    if (!validId(id)) return failure('VALIDATION_ERROR');
    const r = await this.views(); if (!r.ok) return r;
    const view = r.value.find(v => v.execution.executionId === id); return view ? { ok: true, value: view } : failure('NOT_FOUND');
  }
  async readExecutionForPlannedSession(id: string): Promise<TrainingResult<ExecutedSessionView | null>> {
    if (!validId(id)) return failure('VALIDATION_ERROR');
    const r = await this.views(); return r.ok ? { ok: true, value: r.value.find(v => v.execution.plannedSessionId === id) ?? null } : r;
  }
  listExecutionHistory() { return this.views(); }
  listSessionHistory(): Promise<TrainingResult<readonly SessionHistoryView[]>> {
    return this.transaction('readonly', async tx => {
      const checked = await this.control(tx); if (!checked.ok) return checked;
      const context = { datasetId: checked.value.activeDatasetId, controlEpoch: checked.value.controlEpoch };
      const state = await this.snapshot(tx, context.datasetId); if (!state.ok) return state;
      const entries: SessionHistoryView[] = [], dates = new Set<string>();
      let cursor = await tx.objectStore('training_planned_sessions').openCursor(scope(context.datasetId));
      while (cursor) {
        const row = cursor.value;
        if (!row || row.datasetId !== context.datasetId || !validPlannedSession(row.record)
          || cursor.key[1] !== row.record.sessionId || dates.has(row.record.plannedLocalDate)) return failure('CORRUPTION_DETECTED');
        const history = await tx.objectStore('training_planned_session_history').get([context.datasetId, row.record.sessionId, row.record.revision]);
        if (!history || history.datasetId !== context.datasetId || canonical(history.record) !== canonical(row.record)) return failure('CORRUPTION_DETECTED');
        const execution = state.value.executions.find(e => e.plannedSessionId === row.record.sessionId);
        entries.push({ session: row.record, context, ...(execution ? { execution } : {}) });
        dates.add(row.record.plannedLocalDate); cursor = await cursor.continue();
      }
      return { ok: true, value: entries };
    });
  }
  private async mutate(action: 'start' | 'set' | 'complete' | 'cancel', input: Command): Promise<TrainingResult<Commit>> {
    if (!validMutation(input)) return failure('VALIDATION_ERROR');
    let c: Command, fingerprint: string;
    try { c = structuredClone(input); fingerprint = canonical({ kind: 'execution', action, command: c }); } catch { return failure('VALIDATION_ERROR'); }
    if (action === 'start' ? !('execution' in c) || !validExecutedSession(c.execution) || !validRevision(c.expectedPlanRevision)
      : action === 'set' ? !('workingSet' in c) || !validWorkingSet(c.workingSet)
        : !('executionId' in c) || !validId(c.executionId) || typeof c.endedInstant !== 'string' || Number.isNaN(Date.parse(c.endedInstant))) return failure('VALIDATION_ERROR');
    const executionId = 'execution' in c ? c.execution.executionId : 'workingSet' in c ? c.workingSet.executedSessionId : c.executionId;
    let lostAck = false;
    const result = await this.transaction<'readwrite', Commit>('readwrite', async tx => {
      const checked = await this.control(tx, c.context); if (!checked.ok) return checked;
      const control = checked.value, datasetId = control.activeDatasetId;
      const prior = await tx.objectStore('training_receipts').get([datasetId, c.operationId]);
      if (prior) {
        if (prior.datasetId !== datasetId || !validTrainingReceipt(prior.record) || prior.record.operationId !== c.operationId || prior.record.datasetId !== datasetId) return failure('CORRUPTION_DETECTED');
        if (prior.record.fingerprint !== fingerprint) return failure('INVARIANT_CONFLICT');
        if (prior.record.kind !== 'execution' || prior.record.action !== action || prior.record.revisionRef !== executionId
          || prior.record.receipt.ownerId !== ('workingSet' in c ? c.workingSet.workingSetId : executionId)) return failure('CORRUPTION_DETECTED');
        // Start/cancel receipts survive a zero-set cancellation; replay never resurrects a deleted execution.
        return { ok: true, value: { status: 'REPLAYED', receipt: prior.record.receipt,
          ...(prior.record.cancelEffect ? { effect: prior.record.cancelEffect } : {}) } };
      }
      const snapshot = await this.snapshot(tx, datasetId); if (!snapshot.ok) return snapshot;
      if (!Number.isSafeInteger(control.datasetRevision + 1)) return failure('INVARIANT_CONFLICT');
      const fault = this.faults.take(), current = snapshot.value.executions.find(e => e.executionId === executionId);
      let effect: CancelSessionEffect | undefined;
      if (action === 'start' && 'execution' in c) {
        const row = await tx.objectStore('training_planned_sessions').get([datasetId, c.execution.plannedSessionId]);
        if (!row) return failure('NOT_FOUND');
        if (row.datasetId !== datasetId || !validPlannedSession(row.record) || row.record.sessionId !== c.execution.plannedSessionId) return failure('CORRUPTION_DETECTED');
        const history = await tx.objectStore('training_planned_session_history').get([datasetId, row.record.sessionId, row.record.revision]);
        if (!history || history.datasetId !== datasetId || canonical(history.record) !== canonical(row.record)) return failure('CORRUPTION_DETECTED');
        const started = startPlannedSession(row.record, { executionId, sessionId: row.record.sessionId,
          expectedPlanRevision: c.expectedPlanRevision, startedInstant: c.execution.startedInstant }, snapshot.value.executions);
        if (!started.ok) return started;
        if (canonical(started.value) !== canonical(c.execution)) return failure('INVARIANT_CONFLICT');
        // A cancelled execution's identity remains reserved by its start receipt.
        const receipts = await tx.objectStore('training_receipts').getAll(scope(datasetId));
        if (receipts.some(r => validTrainingReceipt(r.record) && r.record.kind === 'execution' && r.record.action === 'start' && r.record.revisionRef === executionId)) return failure('INVARIANT_CONFLICT');
        await tx.objectStore('training_executions').add({ datasetId, record: started.value }, [datasetId, executionId]);
        inject(fault, 'after-claim'); inject(fault, 'after-execution');
      } else {
        if (!current) return failure('NOT_FOUND');
        if (action === 'set' && 'workingSet' in c) {
          if (current.endedInstant !== undefined) return failure('INVARIANT_CONFLICT');
          if (snapshot.value.sets.some(s => s.record.workingSetId === c.workingSet.workingSetId)) return failure('INVARIANT_CONFLICT');
          const exercise = await tx.objectStore('training_exercise_revisions').get([datasetId, c.workingSet.exerciseRevisionRef]);
          if (!exercise) return failure('NOT_FOUND');
          if (exercise.datasetId !== datasetId || !validExerciseRevision(exercise.record) || exercise.record.exerciseRevisionId !== c.workingSet.exerciseRevisionRef) return failure('CORRUPTION_DETECTED');
          const position = snapshot.value.sets.reduce((max, s) => s.record.executedSessionId === executionId ? Math.max(max, s.position) : max, 0) + 1;
          if (!Number.isSafeInteger(position)) return failure('INVARIANT_CONFLICT');
          await tx.objectStore('training_working_sets').add({ datasetId, record: c.workingSet, position }, [datasetId, c.workingSet.workingSetId]);
          inject(fault, 'after-set');
        } else if ('endedInstant' in c) {
          if (action === 'complete') {
            const ended = completeExecution(current, c); if (!ended.ok) return ended;
            await tx.objectStore('training_executions').put({ datasetId, record: ended.value }, [datasetId, executionId]);
          } else {
            const ended = cancelExecution(current, c, snapshot.value.sets.filter(s => s.record.executedSessionId === executionId).length); if (!ended.ok) return ended;
            effect = ended.value.status;
            if (ended.value.status === 'RETURN_TO_PLANNED') await tx.objectStore('training_executions').delete([datasetId, executionId]);
            else await tx.objectStore('training_executions').put({ datasetId, record: ended.value.execution }, [datasetId, executionId]);
          }
          inject(fault, 'after-execution');
        } else return failure('VALIDATION_ERROR');
      }
      const receipt = { operationId: c.operationId, ownerId: 'workingSet' in c ? c.workingSet.workingSetId : executionId };
      const record: StoredTrainingReceipt = { operationId: c.operationId, datasetId, kind: 'execution', action,
        revisionRef: executionId, fingerprint, receipt, ...(effect ? { cancelEffect: effect } : {}) };
      await tx.objectStore('training_receipts').add({ datasetId, record }, [datasetId, c.operationId]); inject(fault, 'after-receipt');
      await tx.objectStore('control').put({ ...control, datasetRevision: control.datasetRevision + 1 }); inject(fault, 'after-control');
      lostAck = fault === 'lost-ack';
      return { ok: true, value: { status: 'APPLIED', receipt, ...(effect ? { effect } : {}) } };
    });
    return result.ok && lostAck ? failure('STORAGE_UNAVAILABLE') : result;
  }
  startSession(c: StartSessionCommand) { return this.mutate('start', c); }
  addWorkingSet(c: AddWorkingSetCommand) { return this.mutate('set', c); }
  completeSession(c: CompleteSessionCommand) { return this.mutate('complete', c); }
  async cancelSession(c: CancelSessionCommand): Promise<TrainingResult<CancelSessionCommitResult>> {
    const r = await this.mutate('cancel', c);
    if (!r.ok) return r;
    return r.value.effect ? { ok: true, value: { ...r.value, effect: r.value.effect } } : failure('CORRUPTION_DETECTED');
  }
}
export async function openTrainingSessions(name: string, faults = new SessionLabFaults()): Promise<TrainingResult<IndexedTrainingSessions>> {
  const r = await openFenixDatabase(name); return r.ok ? { ok: true, value: new IndexedTrainingSessions(r.value, faults) } : r;
}
